import { Node } from "@tiptap/pm/model";
import { NodeSelection } from "@tiptap/pm/state";
import { Editor } from "@tiptap/react";
import { useCallback } from "react";

import { INTERNAL_NODE_CLIPBOARD_MIME } from "pages/tip-tap-editor/utils/pasteHtml";
import { ImageBlockTypes } from "pages/tip-tap-editor/extensions/ImageBlock";
import { useAppDispatch } from "utils/store";
import { removeImageIfOrphaned } from "utils/api";

const useContentItemActions = (editor: Editor, currentNode: Node | null, currentNodePos: number) => {
  const dispatch = useAppDispatch();

  const resetTextFormatting = useCallback(() => {
    const chain = editor.chain();

    chain.setNodeSelection(currentNodePos).unsetAllMarks();

    if (currentNode?.type.name !== "paragraph") {
      chain.setParagraph();
    }

    chain.run();
  }, [editor, currentNodePos, currentNode?.type.name]);

  const duplicateNode = useCallback(() => {
    editor.commands.setNodeSelection(currentNodePos);

    const { $anchor } = editor.state.selection;
    const selectedNode = $anchor.node(1) || (editor.state.selection as NodeSelection).node;

    editor
      .chain()
      .setMeta("hideDragHandle", true)
      .insertContentAt(currentNodePos + (currentNode?.nodeSize || 0), selectedNode.toJSON())
      .run();
  }, [editor, currentNodePos, currentNode?.nodeSize]);

  const copyNodeToClipboard = useCallback(() => {
    editor.chain().setMeta("hideDragHandle", true).setNodeSelection(currentNodePos).run();

    const { $anchor } = editor.state.selection;
    const selectedNode = $anchor.node(1) || (editor.state.selection as NodeSelection).node;

    // Stashes the node's raw JSON as a custom clipboard MIME type alongside the browser's own
    // HTML/text data (multiple listeners on the same copy event can each set different MIME
    // types without conflict) — see INTERNAL_NODE_CLIPBOARD_MIME's comment for why this is
    // necessary rather than relying on HTML round-tripping (ProseMirror's own or ours): some
    // nodes, e.g. images, can't be reconstructed from HTML at all in this schema.
    const stashNodeJson = (event: ClipboardEvent) => {
      event.clipboardData?.setData(INTERNAL_NODE_CLIPBOARD_MIME, JSON.stringify(selectedNode.toJSON()));
    };
    editor.view.dom.addEventListener("copy", stashNodeJson, { once: true });

    document.execCommand("copy");
  }, [editor, currentNodePos]);

  const deleteNode = useCallback(() => {
    const imageId = currentNode?.attrs?.imageId;
    if (currentNode?.type.name === "imageBlock" && currentNode.attrs?.type === ImageBlockTypes.IMAGE && imageId) {
      dispatch(removeImageIfOrphaned(imageId));
    }
    editor.chain().setMeta("hideDragHandle", true).setNodeSelection(currentNodePos).deleteSelection().run();
  }, [editor, currentNodePos, currentNode, dispatch]);

  const handleAdd = useCallback(() => {
    if (currentNodePos !== -1) {
      const currentNodeSize = currentNode?.nodeSize || 0;
      const insertPos = currentNodePos + currentNodeSize;
      const currentNodeIsEmptyParagraph = currentNode?.type.name === "paragraph" && currentNode?.content?.size === 0;
      const focusPos = currentNodeIsEmptyParagraph ? currentNodePos + 2 : insertPos + 2;

      editor
        .chain()
        .command(({ dispatch, tr, state }) => {
          if (dispatch) {
            if (currentNodeIsEmptyParagraph) {
              tr.insertText("/", currentNodePos, currentNodePos + 1);
            } else {
              tr.insert(insertPos, state.schema.nodes.paragraph.create(null, [state.schema.text("/")]));
            }

            return dispatch(tr);
          }

          return true;
        })
        .focus(focusPos)
        .run();
    }
  }, [currentNode, currentNodePos, editor]);

  return {
    resetTextFormatting,
    duplicateNode,
    copyNodeToClipboard,
    deleteNode,
    handleAdd,
  };
};

export default useContentItemActions;
